import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { catchError, take } from 'rxjs/operators';

import { ConfigService } from '@venecia/services';
import { UrlBuilder } from '@venecia/builders';
import { ApiResponse, TemplateHelper } from '@venecia/apis/base';

import { FlowToken } from './model/flow-token.model';
import { FlowPurpose } from './model/flow-purpose.model';
import { FlowValidationResponse } from './model/flow-validation.model';


@Injectable({ providedIn: 'root' })
export class FlowApi {
    private readonly http = inject(HttpClient);
    private readonly configService = inject(ConfigService);

    private urlBuilder?: UrlBuilder;

    protected constructor() {
        this.configService
            .select('http.rest.auth.url')
            .pipe(take(1))
            .subscribe((value: string) => {
                this.urlBuilder = new UrlBuilder(value, '/api/flow');
            });
    }

    getFlowToken(flowCode: string): Observable<ApiResponse<FlowToken>> {
        return this.requestFlowToken(flowCode);
    }

    validate(flowCode: string): Observable<ApiResponse<FlowValidationResponse>> {
        return this.requestFlowToken(flowCode).pipe(
            map((response) => ({
                ...response,
                body: this.toValidationResponse(response.body),
            }))
        );
    }

    private requestFlowToken(flowCode: string): Observable<ApiResponse<FlowToken>> {
        const url = this.urlBuilder!.build('/validate', {
            code: flowCode,
        });

        return this.http
            .post<ApiResponse<FlowToken>>(url, null)
            .pipe(
                TemplateHelper.applyRetry(),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }

    private toValidationResponse(flowToken: FlowToken | null): FlowValidationResponse {
        return {
            accessToken: flowToken?.['access-token'] ?? '',
            redirectTo: flowToken?.['redirect-to'] ?? null,
            purpose: (flowToken?.purpose ?? FlowPurpose.LOGIN),
            expiresAt: flowToken?.['expires-at'] ?? '',
        };
    }
}
